import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { PagesPaginateModel } from "../../../../models/Paginate/PagesPaginate";
import { PricesService } from "../../../../services/Financial/Plans/Prices/PricesServices";
import { ToastSettings } from "../../../../utils/ToastSettings";
import { PricesModel } from "../../../../models/Financial/Prices/PricesModel";

interface ContextProps {
  params: any;
  setParams: Dispatch<SetStateAction<any>>;
  pages: PagesPaginateModel;
  prices: PricesModel[];
  setPages: Dispatch<SetStateAction<PagesPaginateModel>>;
  isLoading: boolean;
  isLoadingModal: boolean;
  error: string;
  setError: Dispatch<SetStateAction<string>>;
  handleList: () => void;
  handleFetch: (data: any) => Promise<boolean>;
  handleDelete: (id: number) => Promise<boolean>;
}

export const PricesContext = createContext<ContextProps>({} as ContextProps);

interface PricesProviderProps {
  children: ReactNode;
}

export const PricesProvider = ({ children }: PricesProviderProps) => {
  const [params, setParams] = useState<any>(null);
  const [pages, setPages] = useState<PagesPaginateModel>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingModal, setIsLoadingModal] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [prices, setPrices] = useState<PricesModel[]>([]);

  const service = new PricesService();
  const location = useLocation();

  const handleList = useCallback(async () => {
    if (!params || !location.pathname.includes("/financial/plans")) {
      return false;
    }

    try {
      setIsLoadingModal(true);
      const [_Response, _Error] = await service.list(params);
      setIsLoadingModal(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setPrices(_Response?.data?.items);
      setPages(_Response?.data?.pagination);
      return setError("");
    } catch (e) {
      setIsLoadingModal(false);
      setError("Não foi possível carregar os preços do sistema.");
    }
  }, [params, location.pathname]);

  const handleFetch = async (data: any) => {
    try {
      setIsLoadingModal(true);
      const [_Response, _Error, _Code, _Errors] = await service.fetch(data);
      setIsLoadingModal(false);
      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "bottom-center",
          "error",
          () => {},
          _Errors,
        );
        return false;
      }

      setError("");
      return true;
    } catch (e) {
      setError("Houve um erro ao salvar plano.");
      setIsLoadingModal(false);
      return false;
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.delete(id);
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(
        e.response?.data?.message || "Houve um erro ao deletar o exemplo",
      );
      return false;
    }
  };

  useEffect(() => {
    !!error && toast.error(error);
  }, [error]);

  useEffect(() => {
    handleList().then();
  }, [handleList]);

  return (
    <PricesContext.Provider
      value={{
        params,
        prices,
        setParams,
        pages,
        setPages,
        isLoading,
        isLoadingModal,
        error,
        setError,
        handleList,
        handleFetch,
        handleDelete,
      }}
    >
      {children}
    </PricesContext.Provider>
  );
};

export const usePrices = () => useContext(PricesContext);
